import appConfig from "../appconfig";

// Private function, not accessible from outside
const getPlugin = () => {
	if (!window.MCCordovaPlugin) {
		throw new Error("MCCordovaPlugin is not available. Make sure the plugin is properly installed and initialized.");
	}
	return window.MCCordovaPlugin;
};

/**
 * Permission states matching Capacitor's implementation
 * @typedef {'prompt' | 'prompt-with-rationale' | 'granted' | 'denied'} PermissionState
 */

/**
 * Check if push notifications are enabled in the SDK
 * @returns {Promise<boolean>} Promise that resolves to true if push is enabled in SDK, false otherwise
 */
export const checkSFMCPushPermission = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.isPushEnabled(
			(enabled) => resolve(enabled),
			(error) => reject(error)
		);
	});
};

/**
 * Check system-level notification permission status
 * @returns {Promise<PermissionState>} Promise that resolves to the current permission state
 */
export const checkSFMCPushSystemPermission = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.checkNotificationPermission(
			(status) => {
				// Map the native status to Capacitor-style permission states
				switch (status) {
				case "prompt":
				case "prompt-with-rationale":
				case "granted":
				case "denied":
					resolve(status);
					break;
				default:
				// If we get a boolean, map it to the appropriate state
					if (typeof status === "boolean") {
						resolve(status ? "granted" : "denied");
					} else {
						resolve("prompt");
					}
				}
			},
			(error) => reject(error)
		);
	});
};

/**
 * Request push notification permission
 * @returns {Promise<PermissionState>} Promise that resolves to the new permission state
 */
export const requestSFMCPushPermission = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.requestPushPermission(
			(status) => {
				// Map the native status to Capacitor-style permission states
				switch (status) {
				case "prompt":
				case "prompt-with-rationale":
				case "granted":
				case "denied":
					resolve(status);
					break;
				default:
				// If we get a boolean, map it to the appropriate state
					if (typeof status === "boolean") {
						resolve(status ? "granted" : "denied");
					} else {
						resolve("prompt");
					}
				}
			},
			(error) => reject(error)
		);
	});
};

/**
 * Enable push notifications in the SDK only
 * Note: This only enables the SDK's internal push state and does not affect system-level notification permissions
 * @returns {Promise<void>}
 */
export const enableSFMCPushNotifications = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.enablePush(
			() => resolve(),
			(error) => reject(error)
		);
	});
};

/**
 * Disable push notifications in the SDK only
 * Note: This only disables the SDK's internal push state and does not affect system-level notification permissions
 * @returns {Promise<void>}
 */
export const disableSFMCPushNotifications = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.disablePush(
			() => resolve(),
			(error) => reject(error)
		);
	});
};

/**
 * Set the contact key for the device's user
 * @param {string} contactKey - The value to be set as the contact key
 * @returns {Promise<boolean>} Promise that resolves to true if the contact key was set successfully
 */
export const setSFMCContactKey = (contactKey) => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.setContactKey(
			contactKey,
			(saved) => resolve(saved),
			(error) => reject(error)
		);
	});
};

/**
 * Enable logging for the SFMC SDK
 * @returns {Promise<void>}
 */
export const enableSFMCLogging = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.enableLogging(
			() => resolve(),
			(error) => reject(error)
		);
	});
};

/**
 * Log the current state of the SFMC SDK for debugging purposes
 * @returns {Promise<void>}
 */
export const logSFMCSDKState = async () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.logSdkState(
			() => resolve(),
			(error) => reject(error)
		);
	});
};

/**
 * Get the current contact key for the device's user
 * @returns {Promise<string>} Promise that resolves to the current contact key
 */
export const getSFMCContactKey = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.getContactKey(
			(contactKey) => resolve(contactKey),
			(error) => reject(error)
		);
	});
};